import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MediaService } from '../../../shared/services/media.service';
import {
  getMediaUploadFileError,
  IMAGE_MIME_EXTENSION_FALLBACKS,
  needsServerImagePreview,
} from '../../../shared/utils/accepted-image-formats.util';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileUploadComponent,
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor {
  onChange: (file: File | null) => {};
  @Output() delete: EventEmitter<any> = new EventEmitter();
  @Output() upload: EventEmitter<any> = new EventEmitter();
  @Output() startUpload: EventEmitter<any> = new EventEmitter();
  @Output() finished: EventEmitter<any> = new EventEmitter();
  @Output() fail: EventEmitter<any> = new EventEmitter();
  @Input() preview: string | ArrayBuffer | SafeUrl;
  @Input() id: number;
  @Input() isDeletable = true;
  /** Callers must set this (e.g. images vs PDF). Empty = no client-side type filter. */
  @Input() accept: string;
  @Input() clearAfterUpload = false;
  file: File | null = null;
  isUploading = false;

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private toastrService: ToastrService,
    private sanitizer: DomSanitizer,
    private mediaService: MediaService,
  ) {}
  @ViewChild('fileInput') fileInput: ElementRef;
  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event?.item(0);
    this.file = file;
    this.startUpload.emit('started');
    if (!this.file) return;

    if (this.accept && !this.isFileAccepted(this.file)) {
      this.toastrService.error(
        `Only files with the following extensions are allowed: ${this.accept}`,
      );
      this.fail.emit();
      this.clearInput();
      return;
    }

    this.isUploading = true;
    // Local preview for formats the browser can render; HEIC/HEIF/BMP wait for server PNG.
    if (
      this.file &&
      (this.file.type || '').startsWith('image/') &&
      !needsServerImagePreview(this.file.name)
    ) {
      const reader = new FileReader();
      reader.readAsDataURL(this.file);
      reader.onload = () => {
        this.preview = reader.result;
      };
    }

    this.mediaService.uploadMedia({ file: this.file }).subscribe({
      next: (res) => {
        const resWithSize = {
          ...res,
          fileSize: this.file?.size || 0,
        };
        // Prefer server link for images (converted PNG for HEIC/HEIF/BMP).
        if (res?.link && this.isImageFile(this.file)) {
          this.preview = res.link;
        }
        this.upload.emit(resWithSize);
        this.id = res.id;
        if (this.clearAfterUpload) {
          this.clear();
        }
      },
      error: (error) => {
        const message =
          getMediaUploadFileError(error) ||
          'Upload failed. Please try another image.';
        this.toastrService.error(message);
        this.isUploading = false;
        this.fail.emit();
        this.clearInput();
      },
      complete: () => {
        this.isUploading = false;
        this.finished.emit('finished');
      },
    });
  }
  clickInput() {
    this.fileInput.nativeElement.click();
  }
  clickFileInput() {
    this.fileInput.nativeElement.click();
  }
  writeValue(value: null) {
    this.preview = value;
    this.host.nativeElement.value = '';
    this.file = null;
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    console.log(fn);
  }

  clear() {
    this.file = null;
    this.preview = null;
    this.isUploading = false;
    this.delete.emit(this.id);
    this.id = null;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  getImageUrl(url) {
    return this.sanitizer.bypassSecurityTrustUrl(url as string);
  }

  private isFileAccepted(file: File): boolean {
    if (!this.accept) {
      return true;
    }

    const accepted = this.accept
      .split(',')
      .map((token: string) => token.trim().toLowerCase())
      .filter(Boolean);
    if (!accepted.length) {
      return true;
    }

    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    const fileExtension = name.includes('.')
      ? name.substring(name.lastIndexOf('.'))
      : '';

    return accepted.some((pattern: string) => {
      if (pattern.startsWith('.')) {
        return name.endsWith(pattern);
      }
      if (pattern.endsWith('/*')) {
        return type.startsWith(pattern.slice(0, -1));
      }
      if (pattern.includes('/')) {
        if (type && type === pattern) {
          return true;
        }
        const fallbackExts = IMAGE_MIME_EXTENSION_FALLBACKS[pattern];
        return !!fallbackExts?.some((ext) => name.endsWith(ext));
      }
      // Bare extension without a leading dot (e.g. "png")
      const normalizedExt = `.${pattern}`;
      return fileExtension === normalizedExt || name.endsWith(normalizedExt);
    });
  }

  private isImageFile(file: File | null): boolean {
    if (!file) {
      return false;
    }
    if ((file.type || '').startsWith('image/')) {
      return true;
    }
    return /\.(png|jpe?g|heic|heif|bmp|webp)$/i.test(file.name || '');
  }

  private clearInput() {
    this.file = null;
    this.preview = null;
    this.isUploading = false;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    if (this.host?.nativeElement) {
      this.host.nativeElement.value = '';
    }
  }
}
